'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useCallback, useState } from 'react';
import { CaretUp } from '../../../../assets/icons';
import { useWindowSize } from '../../../../hooks/dom/useWindowSize';
import { VideoPlayer } from '../../../shared';
import { CustomImage as Image } from '../../../shared';
import { AirRingColors } from 'content/products/ring';
import styles from './Carousal.module.css';

export const Carousal = ({
  className,
  items,
}: {
  className?: string;
  items: ReadonlyArray<Record<string, string | boolean | undefined>>;
  selectedColor?: AirRingColors;
}) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
      align: 'center',
      loop: true,
      slidesToScroll: 1,
    });

    const [activeSlide, setActiveSlide] = useState<number | string | undefined>(
      0,
    );

    const windowSize: Record<string, number | undefined> = useWindowSize();

    const scrollPrev = useCallback(() => {
      if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    const onSelect = useCallback(() => {
      if (!emblaApi) return;
      setActiveSlide(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
      if (!emblaApi) return;
      onSelect();
      emblaApi.on('select', onSelect);
      return () => {
        emblaApi.off('select', onSelect);
      };
    }, [emblaApi, onSelect]);

    useEffect(() => {
      if (document) {
        const images = document.getElementsByTagName('img');
        for (const item of images) {
          item.onload = () => {
            item.style.background = 'transparent';
          };
        }
      }
    }, []);

    return (
      <div className={className ? `${styles.root} ${className}` : styles.root}>
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {items?.map(
              (
                element: Record<string, string | boolean | undefined>,
                index,
              ) => {
                return (
                  <div className="embla__slide" key={index}>
                    <div className="carousal-img-container">
                      {!element.video && (
                        <div className="element-img">
                          <Image
                            src={
                              (windowSize.width as number) >
                              (windowSize.height as number)
                                ? (element.src as string)
                                : ((element.srcResponsive ??
                                    element.src) as string)
                            }
                            alt={element.alt as string}
                            fill
                            className={`custom-img`}
                            style={{
                              objectFit: 'cover',
                              objectPosition: element.objectPositionBottom
                                ? 'bottom'
                                : 'center',
                            }}
                            priority={index <= 1}
                          />
                        </div>
                      )}
                      {element.video && (
                        <VideoPlayer
                          playable={activeSlide == index}
                          videoProps={{
                            src: element.src as string,
                            poster: element.poster as string,
                            preload: 'true',
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
        <button
          className="embla__prev"
          onClick={scrollPrev}
          type="button"
          aria-label="Previous image"
        >
          <div className="circle-blur-btn">
            <CaretUp style={{ transform: 'rotate(-90deg)' }} fill="black" />
          </div>
        </button>
        <button
          className="embla__next"
          onClick={scrollNext}
          type="button"
          aria-label="Next image"
        >
          <div className="circle-blur-btn">
            <CaretUp style={{ transform: 'rotate(90deg)' }} fill="black" />
          </div>
        </button>
      </div>
    );
};
